// Cliente HTTP do painel Super Master (admin.gpsdrive.com.br) — sessão
// TOTALMENTE separada da sessão de empresa (gpsdrive-client.ts): backend
// próprio (PLATFORM_JWT_SECRET/PLATFORM_JWT_REFRESH_SECRET, ver
// platform-auth.controller.ts), cookie de refresh próprio
// (gpsdrive_platform_refresh, path /api/v1/platform-auth), access token
// próprio em memória. Nunca compartilha token/estado com
// lib/api/gpsdrive-client.ts — um PlatformAdmin não é um User de empresa e
// vice-versa.
import { GPSDRIVE_API_URL } from "./gpsdrive-client";
import { fetchWithNetworkRetry } from "./fetch-retry";

export class PlatformApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;

export function setPlatformAccessToken(token: string | null): void {
  accessToken = token;
}

export function getPlatformAccessToken(): string | null {
  return accessToken;
}

export function setPlatformSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

function requireBaseUrl(): string {
  if (!GPSDRIVE_API_URL) {
    throw new PlatformApiError("VITE_GPSDRIVE_API_URL não configurada", 0);
  }
  return GPSDRIVE_API_URL;
}

// Single-flight igual ao de gpsdrive-client.ts (mesmo motivo: nunca deixar
// duas chamadas de refresh concorrentes rotacionarem o mesmo cookie).
let refreshInFlight: Promise<PlatformRefreshResult | null> | null = null;

export interface PlatformRefreshResult {
  accessToken: string;
  admin: unknown;
}

export async function refreshPlatformSession(): Promise<PlatformRefreshResult | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function performRefresh(): Promise<PlatformRefreshResult | null> {
  const res = await fetch(`${requireBaseUrl()}/api/v1/platform-auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    accessToken = null;
    onSessionExpired?.();
    return null;
  }
  const data = (await res.json()) as { accessToken: string; admin: unknown };
  accessToken = data.accessToken;
  return { accessToken: data.accessToken, admin: data.admin };
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

async function parseErrorMessage(res: Response, path: string): Promise<string> {
  const detail = await res.json().catch(() => undefined);
  return (detail as { message?: string })?.message ?? `Erro ${res.status} ao acessar ${path}`;
}

async function fetchWithAuth(path: string, init: RequestInit, isRetry = false): Promise<Response> {
  const baseUrl = requireBaseUrl();
  const headers = new Headers(init.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const res = await fetchWithNetworkRetry(`${baseUrl}${path}`, { ...init, headers, credentials: "include" });

  if (res.status === 401 && !isRetry) {
    const renewed = await refreshPlatformSession();
    if (renewed) {
      return fetchWithAuth(path, init, true);
    }
  }

  return res;
}

export async function platformRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const res = await fetchWithAuth(path, {
    ...rest,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(headers ?? {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    throw new PlatformApiError(await parseErrorMessage(res, path), res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const platformGet = <T>(path: string) => platformRequest<T>(path, { method: "GET" });
export const platformPost = <T>(path: string, body?: unknown) =>
  platformRequest<T>(path, { method: "POST", body });
export const platformPatch = <T>(path: string, body?: unknown) =>
  platformRequest<T>(path, { method: "PATCH", body });
export const platformPut = <T>(path: string, body?: unknown) =>
  platformRequest<T>(path, { method: "PUT", body });

export async function platformDelete(path: string): Promise<void> {
  const res = await fetchWithAuth(path, { method: "DELETE", headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new PlatformApiError(await parseErrorMessage(res, path), res.status);
  }
}

// Upload multipart (som de notificação) — mesmo motivo de gpsDriveUpload em
// gpsdrive-client.ts: não passa por platformRequest porque não deve mandar
// Content-Type manual (o browser define o boundary do multipart sozinho).
export async function platformUploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);

  const res = await fetchWithAuth(path, { method: "POST", body: form, headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new PlatformApiError(await parseErrorMessage(res, path), res.status);
  }
  return (await res.json()) as T;
}

// POST de corpo binário cru (ex.: áudio de locução da câmera JT808) —
// diferente de platformUploadFile (multipart/FormData): aqui o Content-Type
// é fixo e o corpo é o Blob/ArrayBuffer inteiro, sem boundary nenhum.
export async function platformPostBinary<T>(path: string, body: Blob, contentType: string): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: "POST",
    body,
    headers: { Accept: "application/json", "Content-Type": contentType },
  });
  if (!res.ok) {
    throw new PlatformApiError(await parseErrorMessage(res, path), res.status);
  }
  return (await res.json()) as T;
}

// Busca binário autenticado (ex.: foto de alarme da câmera JT808) como
// Blob — reaproveita fetchWithAuth (token + retry em 401), diferente de
// handleDownload em master.app.tsx (aquele é sobre um link público de
// storage, sem Authorization nenhum).
export async function platformFetchBlob(path: string): Promise<Blob> {
  const res = await fetchWithAuth(path, { headers: { Accept: "*/*" } });
  if (!res.ok) {
    throw new PlatformApiError(await parseErrorMessage(res, path), res.status);
  }
  return res.blob();
}

// Mesma ideia de gpsDriveUploadWithFields em gpsdrive-client.ts (aba
// DOCUMENTOS) — campos extras (ex.: "version" do APK, painel Aplicativo)
// vão ANTES do arquivo no FormData, senão o backend (@fastify/multipart)
// não consegue ler file.fields.<nome> a tempo (ver comentário lá).
export async function platformUploadFileWithFields<T>(
  path: string,
  file: File,
  fields: Record<string, string>,
): Promise<T> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) form.append(key, value);
  form.append("file", file);

  const res = await fetchWithAuth(path, { method: "POST", body: form, headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new PlatformApiError(await parseErrorMessage(res, path), res.status);
  }
  return (await res.json()) as T;
}
